import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { routePath, routeName } from '@/constants/route.constants'
import { authGuard } from './guards'

// Declare route meta types for TypeScript
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    requiresAdmin?: boolean
    requiresAuth?: boolean
    requiresVeterinarian?: boolean
    requiresAcceptingTerms?: boolean
    requiresTagHasNoMatch?: boolean
    requiresOwnerAuthentication?: boolean
  }
}

// View components (lazy loaded)
const LoginView = () => import('@/views/login/LoginView.vue')
const ResetPasswordView = () => import('@/views/password/ResetPasswordView.vue')
const ChangeEmailView = () => import('@/views/email/ChangeEmailView.vue')
const AdminView = () => import('@/views/admin/AdminView.vue')
const VetView = () => import('@/views/veterinarian/VetView.vue')
const PublicView = () => import('@/views/public/PublicView.vue')

// Admin components
const AdminUserComponent = () => import('@/components/admin/AdminUser.vue')
const AccountListComponent = () => import('@/components/account/AccountList.vue')
const AccountFormComponent = () => import('@/components/account/AccountForm.vue')
const AccountUserFormComponent = () => import('@/components/account/AccountUserForm.vue')

// Registration components
const RegistrationFormComponent = () => import('@/components/registration/RegistrationForm.vue')
const RegistrationListComponent = () => import('@/components/registration/RegistrationList.vue')
const CustomerFormComponent = () => import('@/components/customer/CustomerForm.vue')

// Public components
const TermsComponent = () => import('@/components/public/Terms.vue')
const ScanCodeComponent = () => import('@/components/public/ScanCode.vue')
const ViewAccessComponent = () => import('@/components/public/ViewAccess.vue')
const OwnerDetailsComponent = () => import('@/components/public/OwnerDetails.vue')
const OwnerAccessComponent = () => import('@/components/public/OwnerAccess.vue')
const OwnerComponent = () => import('@/components/public/OwnerContainer.vue')
const PaymentComponent = () => import('@/components/public/Payment.vue')
const PaymentConfirmationComponent = () => import('@/components/public/PaymentConfirmation.vue')
const QRCodeComponent = () => import('@/components/public/QRCode.vue')
const QRCodeStreamComponent = () => import('@/components/scan/QRCodeStream.vue')

const routes: RouteRecordRaw[] = [
  // #region Redirects
  {
    path: routePath.veterinarian.base,
    redirect: { name: routeName.veterinarian.register },
  },
  {
    path: routePath.admin.base,
    redirect: { name: routeName.admin.accounts },
  },
  // #endregion

  // #region Authentication routes
  {
    path: routePath.login,
    name: routeName.login,
    component: LoginView,
    meta: {
      title: 'Log in to Paw Finders',
    },
  },
  {
    path: routePath.resetPassword,
    name: routeName.resetPassword,
    component: ResetPasswordView,
    meta: {
      title: 'Forgot password? Set a new password in Paw Finders',
    },
  },
  {
    path: routePath.confirmEmailChange,
    name: routeName.confirmEmailChange,
    component: ChangeEmailView,
    meta: {
      title: 'Confirm your new email in Paw Finders',
    },
  },
  // #endregion

  // #region Admin routes
  {
    path: routePath.admin.base,
    component: AdminView,
    children: [
      {
        path: routePath.admin.accounts,
        name: routeName.admin.accounts,
        component: AccountListComponent,
        meta: {
          requiresAdmin: true,
          title: 'Veterinarian accounts in Paw Finders',
        },
      },
      {
        path: routePath.admin.userList,
        name: routeName.admin.userList,
        component: AdminUserComponent,
        meta: {
          requiresAdmin: true,
          title: 'Manage admin users in Paw Finders',
        },
      },
      {
        path: routePath.admin.createAccount,
        name: routeName.admin.createAccount,
        component: AccountFormComponent,
        props: {
          title: 'Create Account',
        },
        meta: {
          requiresAdmin: true,
          title: 'Create veterinarian account in Paw Finders',
        },
      },
      {
        path: routePath.admin.editAccount,
        name: routeName.admin.editAccount,
        component: AccountFormComponent,
        props: {
          title: 'Manage Account',
        },
        meta: {
          requiresAdmin: true,
          title: 'Manage veterinarian account in Paw Finders',
        },
      },
      {
        path: routePath.admin.addUsers,
        name: routeName.admin.addUsers,
        component: AccountUserFormComponent,
        props: {
          title: 'Add Users',
        },
        meta: {
          requiresAdmin: true,
          title: 'Add users to veterinarian account in Paw Finders',
        },
      },
      {
        path: routePath.admin.editUsers,
        name: routeName.admin.editUsers,
        component: AccountUserFormComponent,
        props: {
          title: 'Manage Users',
        },
        meta: {
          requiresAdmin: true,
          title: 'Manage veterinarian account users in Paw Finders',
        },
      },
      {
        path: routePath.admin.registrations,
        name: routeName.admin.registrations,
        component: RegistrationListComponent,
        meta: {
          requiresAdmin: true,
          title: 'Tag registrations in Paw Finders',
        },
      },
      {
        path: routePath.admin.editCustomer,
        name: routeName.admin.editCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Manage Customer Registration',
        },
        meta: {
          requiresAdmin: true,
          title: 'Manage tag registration for customer in Paw Finders',
        },
      },
      // Used when creating customer for pre-registered tag
      {
        path: routePath.admin.createCustomer,
        name: routeName.admin.createCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Register Customer',
        },
        meta: {
          requiresAdmin: true,
          title: 'Add customer to tag registration in Paw Finders',
        },
      },
      // Used when changing ownership
      {
        path: routePath.admin.addCustomer,
        name: routeName.admin.addCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Add Customer to Registration',
        },
        meta: {
          requiresAdmin: true,
          title: 'Add customer to tag registration in Paw Finders',
        },
      },
    ],
  },
  // #endregion

  // #region Veterinarian routes
  {
    path: routePath.veterinarian.base,
    component: VetView,
    children: [
      {
        path: routePath.veterinarian.register,
        name: routeName.veterinarian.register,
        component: RegistrationFormComponent,
        meta: {
          requiresAuth: true,
          title: 'Register a tag with Paw Finders',
        },
      },
      {
        path: routePath.veterinarian.registrations,
        name: routeName.veterinarian.registrations,
        component: RegistrationListComponent,
        meta: {
          requiresAuth: true,
          title: 'Tag registrations in Paw Finders',
        },
      },
      // Used when registering a new tag
      {
        path: routePath.veterinarian.createRegistration,
        name: routeName.veterinarian.createRegistration,
        component: CustomerFormComponent,
        props: {
          title: 'Register Customer',
        },
        meta: {
          requiresAuth: true,
          title: 'Create tag registration for customer in Paw Finders',
        },
      },
      // Used when creating customer for pre-registered tag
      {
        path: routePath.veterinarian.createCustomer,
        name: routeName.veterinarian.createCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Register Customer',
        },
        meta: {
          requiresAuth: true,
          title: 'Add customer to tag registration in Paw Finders',
        },
      },
      // Used when changing ownership
      {
        path: routePath.veterinarian.addCustomer,
        name: routeName.veterinarian.addCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Add Customer to Registration',
        },
        meta: {
          requiresAuth: true,
          title: 'Add customer to tag registration in Paw Finders',
        },
      },
      {
        path: routePath.veterinarian.editCustomer,
        name: routeName.veterinarian.editCustomer,
        component: CustomerFormComponent,
        props: {
          title: 'Manage Customer Registration',
        },
        meta: {
          requiresAuth: true,
          title: 'Manage tag registration for customer in Paw Finders',
        },
      },
      {
        path: routePath.veterinarian.qrCodeStream,
        name: routeName.veterinarian.qrCodeStream,
        component: QRCodeStreamComponent,
        meta: {
          requiresAuth: true,
          title: 'Scan QR Code in Paw Finders',
        },
      },
      {
        path: routePath.veterinarian.editUsers,
        name: routeName.veterinarian.editUsers,
        component: AccountUserFormComponent,
        meta: {
          requiresVeterinarian: true,
          title: 'Add users to veterinarian account in Paw Finders',
        },
      },
    ],
  },
  // #endregion

  // #region Public routes
  {
    path: routePath.base,
    component: PublicView,
    children: [
      {
        path: routePath.public.scan,
        name: routeName.public.scan,
        component: ScanCodeComponent,
        meta: {
          title: 'Scan tag with Paw Finders',
        },
      },
      {
        path: routePath.public.terms,
        name: routeName.public.terms,
        component: TermsComponent,
        meta: {
          title: 'Paw Finders terms & conditions',
        },
      },
      {
        path: routePath.public.qrCodeStream,
        name: routeName.public.qrCodeStream,
        component: QRCodeComponent,
        meta: {
          title: 'Scan QR Code in Paw Finders',
        },
      },
      {
        path: routePath.public.register,
        name: routeName.public.register,
        component: OwnerComponent,
        props: {
          title: 'Create Owner Info',
        },
        meta: {
          requiresTagHasNoMatch: true,
          title: 'Register a tag with Paw Finders',
        },
      },
      {
        path: routePath.public.payment,
        name: routeName.public.payment,
        component: PaymentComponent,
        meta: {
          requiresTagHasNoMatch: true,
          title: 'Pay for tag registration in Paw Finders',
        },
      },
      {
        path: routePath.public.paymentConfirmation,
        name: routeName.public.paymentConfirmation,
        component: PaymentConfirmationComponent,
        meta: {
          requiresTagHasNoMatch: true,
          title: 'Registration confirmation in Paw Finders',
        },
      },
      {
        path: routePath.public.viewAccess,
        name: routeName.public.viewAccess,
        component: ViewAccessComponent,
        props: true,
        meta: {
          title: 'Accept Paw Finders terms of use.',
        },
      },
      {
        path: routePath.public.findTag,
        name: routeName.public.findTag,
        component: ScanCodeComponent,
        props: true,
        meta: {
          title: 'Find a pet or register a tag with Paw Finders',
        },
      },
      {
        path: routePath.public.customer,
        name: routeName.public.customer,
        component: OwnerDetailsComponent,
        meta: {
          requiresAcceptingTerms: true,
          title: 'Owner Details in Paw Finders',
        },
      },
      {
        path: routePath.public.customerAccess,
        name: routeName.public.customerAccess,
        component: OwnerAccessComponent,
        meta: {
          requiresAcceptingTerms: true,
          title: 'Confirm you are the owner in Paw Finders',
        },
      },
      {
        path: routePath.public.editCustomer,
        name: routeName.public.editCustomer,
        component: OwnerComponent,
        props: {
          title: 'Manage Owner Info',
        },
        meta: {
          requiresOwnerAuthentication: true,
          title: 'Manage owner information in Paw Finders',
        },
      },
    ],
  },
  // #endregion
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { left: 0, top: 0 }
    }
  },
})

// Register navigation guards
router.beforeEach(authGuard)

export default router
